import { apiFetch } from "../../lib/api";
import type {
  Inquiry,
  Communication,
  Reminder,
  CallBooking,
  CallBookingStatus,
  AvailabilityBlock,
  DateOverride,
} from "./types";

/** Fired on `window` after any successful Events mutation so nav indicators can refresh. */
export const EVENTS_CHANGED = "hq:events-changed";

async function eventsGet<T>(path: string): Promise<T> {
  const r = await apiFetch(`/events${path}`);
  if (!r.ok) throw new Error(`${r.status}: ${await r.text()}`);
  return (await r.json()) as T;
}

async function eventsSend<T>(path: string, method: string, body?: unknown): Promise<T> {
  const r = await apiFetch(`/events${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`${r.status}: ${text}`);
  }
  const data = (await r.json()) as T;
  window.dispatchEvent(new Event(EVENTS_CHANGED));
  return data;
}

export async function listInquiries(params?: { status?: string; limit?: number; offset?: number }) {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.limit) qs.set("limit", params.limit.toString());
  if (params?.offset) qs.set("offset", params.offset.toString());
  return eventsGet<{ data: Inquiry[] }>(`/inquiries?${qs}`);
}

export async function getInquiry(id: string) {
  return eventsGet<{ data: Inquiry }>(`/inquiries/${id}`);
}

export async function createInquiry(data: Partial<Inquiry>) {
  return eventsSend<{ data: Inquiry }>("/inquiries", "POST", data);
}

export async function updateInquiry(id: string, patch: Partial<Inquiry>) {
  return eventsSend<{ ok: boolean }>(`/inquiries/${id}`, "PATCH", patch);
}

export async function deleteInquiry(id: string) {
  return eventsSend<{ ok: boolean }>(`/inquiries/${id}`, "DELETE");
}

export async function getCommunications(inquiryId: string) {
  return eventsGet<{ data: Communication[] }>(`/inquiries/${inquiryId}/communications`);
}

export async function getUnreadInquiryIds() {
  return eventsGet<{ data: string[] }>("/communications/unread");
}

export async function markInquiryRead(inquiryId: string) {
  return eventsSend<{ ok: boolean }>(`/inquiries/${inquiryId}/read`, "POST");
}

export async function sendCommunication(
  inquiryId: string,
  payload: { channel: Communication["channel"]; subject?: string; message: string; callbackCalled?: boolean }
) {
  return eventsSend<{ data: Communication }>(`/inquiries/${inquiryId}/communicate`, "POST", payload);
}

export async function getReminders(inquiryId: string) {
  return eventsGet<{ data: Reminder[] }>(`/inquiries/${inquiryId}/reminders`);
}

export async function createReminder(
  inquiryId: string,
  payload: { reminder_type: string; scheduled_for: string; channel?: string }
) {
  return eventsSend<{ data: Reminder }>(`/inquiries/${inquiryId}/reminders`, "POST", payload);
}

export async function updateReminder(reminderId: string, patch: Partial<Reminder>) {
  return eventsSend<{ ok: boolean }>(`/reminders/${reminderId}`, "PATCH", patch);
}

export type ReservationAvailability = {
  status: 'available' | 'closed' | 'full' | 'unavailable' | 'not_ready' | 'error';
  bayCount?: number; checkedAt?: string; qualifyingStarts?: string[]; message?: string;
};
export async function getReservationAvailability(id: string, date: string, time?: string) {
  const query = new URLSearchParams({ date });
  if (time) query.set('time', time);
  return eventsGet<{ data: ReservationAvailability }>(`/inquiries/${encodeURIComponent(id)}/availability?${query}`);
}

// ── Scheduled Calls ─────────────────────────────────────────────────

export async function getAllBookings(status?: CallBookingStatus) {
  const qs = status ? `?status=${status}` : "";
  return eventsGet<{ data: CallBooking[] }>(`/scheduling/bookings${qs}`);
}

export async function updateBookingStatus(id: string, status: CallBookingStatus) {
  return eventsSend<{ ok: boolean }>(`/scheduling/bookings/${id}`, "PATCH", { status });
}

export async function updateBookingNotes(id: string, notes: string) {
  return eventsSend<{ ok: boolean }>(`/scheduling/bookings/${id}`, "PATCH", { notes });
}

// ── Call Availability ───────────────────────────────────────────────

export async function getAvailability() {
  return eventsGet<{ data: AvailabilityBlock[] }>("/scheduling/availability");
}

export async function addAvailabilityBlock(block: {
  day_of_week: number;
  start_time: string;
  end_time: string;
}) {
  return eventsSend<{ data: AvailabilityBlock }>("/scheduling/availability", "POST", block);
}

export async function deleteAvailabilityBlock(id: string) {
  return eventsSend<{ ok: boolean }>(`/scheduling/availability/${id}`, "DELETE");
}

export async function getDateOverrides() {
  return eventsGet<{ data: DateOverride[] }>("/scheduling/overrides");
}

export async function addDateOverride(override: {
  date: string;
  override_type: DateOverride["override_type"];
  start_time?: string;
  end_time?: string;
  note?: string;
}) {
  return eventsSend<{ data: DateOverride }>("/scheduling/overrides", "POST", override);
}

export async function deleteDateOverride(id: string) {
  return eventsSend<{ ok: boolean }>(`/scheduling/overrides/${id}`, "DELETE");
}

// ── Settings / Form Config ──────────────────────────────────────────

export type FormConfigMap = Record<string, unknown>;

export async function getFormConfig(): Promise<FormConfigMap> {
  return eventsGet<FormConfigMap>("/settings/form-config");
}

export async function updateFormConfigKey(key: string, value: unknown): Promise<void> {
  await eventsSend<{ ok: boolean }>(`/settings/form-config/${key}`, "PUT", { value });
}

// ── Generate Quote in New System (tribal-axe#10) ────────────────────
// Dormant until the cutover: every route answers 404 not_enabled, and the
// action stays hidden. See HQ/reservation-group-events.md.

/** A refusal from the events Worker or the Reservation System; `retry` means resend unchanged. */
export class ReservationApiError extends Error {
  constructor(readonly status: number, readonly code: string, detail: string, readonly retry: boolean) { super(detail); }
}

async function reservationSend<T>(path: string, method: string, body?: unknown): Promise<T> {
  const r = await apiFetch(`/events${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const answer = await r.json().catch(() => null) as { error?: string; detail?: string; retry?: boolean } | null;
  if (!r.ok) {
    const retry = answer?.retry === true || r.status >= 500;
    throw new ReservationApiError(r.status, answer?.error ?? "request_failed",
      answer?.detail ?? (retry ? "The Reservation System did not answer. Retry." : `Request failed (${r.status}).`), retry);
  }
  return answer as T;
}

export type ReservationCatalog = {
  experiences: Array<{
    id: string; name: string;
    groupGuestLimits: { minimum?: number; maximum?: number } | null;
    groupDepositBps: number | null;
    groupRates: Partial<Record<GroupEventRate, Array<{ minutes: number; pricePerPersonCents: number; cityTaxesAndFeesPerPersonCents?: number }>>>;
  }>;
  items: Array<{ id: string; name: string; kind: string; pricingBasis: "per_person" | "per_item"; unitPriceCents: number }>;
  // The Reservation System's active saved Proposal messages (ReservationSystem #281).
  // Absent before its migration 0080.
  proposalTemplates?: Array<{ id: string; name: string; body: string }>;
};
export type GroupEventRate = "no_food" | "with_food";
export type ReservationQuoteDetails = {
  experienceId: string; groupEventRate: GroupEventRate; localDate: string; startMinute: number;
  durationMinutes: number; partySize: number; items: Array<{ catalogItemId: string; quantity: number }>;
};
export type ReservationQuoteResult = {
  reservationCode: string; reservationStatus: string;
  quote: { id: string; version: number; totalCents: number; depositCents: number };
  depositLink: { url: string; amountCents: number; state: string } | null;
};
export type ReservationStatus = {
  reservationCode: string; reservationStatus: string; paymentStatus: string; receivedCents: number; appliedQuoteVersion: number;
  quote?: { version?: number; totalCents?: number; depositCents?: number; linkValid?: boolean } | null;
  proposals?: Array<{ id: string; state: string; sentAt?: string | null; error?: string | null; stopReason?: string | null; deliveryStatus?: string | null; quoteVersion?: number; createdAt?: string }>;
  reviewItems?: Array<{ reason: string }>;
  depositLink?: { state: string; amountCents: number; quoteVersion: number; paidAt: string | null } | null;
};

/** Resolves null while the feature is switched off. */
export async function getReservationCatalog(): Promise<ReservationCatalog | null> {
  try { return (await reservationSend<{ data: ReservationCatalog }>("/reservation/catalog", "GET")).data; }
  catch (err) { if (err instanceof ReservationApiError && err.code === "not_enabled") return null; throw err; }
}
export async function generateReservationQuote(inquiryId: string, details: ReservationQuoteDetails | { retry: true }) {
  return (await reservationSend<{ data: ReservationQuoteResult }>(`/inquiries/${encodeURIComponent(inquiryId)}/reservation-quote`, "POST", details)).data;
}
export async function sendReservationProposal(inquiryId: string, body: { message?: string } | { retry: true }) {
  return (await reservationSend<{ data: { id: string; state: string } }>(`/inquiries/${encodeURIComponent(inquiryId)}/reservation-proposal`, "POST", body)).data;
}
export async function syncReservation(inquiryId: string) {
  return (await reservationSend<{ data: { status: ReservationStatus; inquiryStatus: string; reservationQuote: Inquiry["reservation_quote"] } }>(`/inquiries/${encodeURIComponent(inquiryId)}/reservation-sync`, "POST", {})).data;
}
// ReservationSystem #484: the guest said no to the Proposal (reason from the pick list), or reopen it.
export async function decideProposal(inquiryId: string, decision: { op: "decline"; reason: string; note?: string } | { op: "reopen"; reason: string }) {
  return (await reservationSend<{ data: { status: ReservationStatus; inquiryStatus: string; reservationQuote: Inquiry["reservation_quote"] } }>(`/inquiries/${encodeURIComponent(inquiryId)}/reservation-decision`, "POST", decision)).data;
}
// Every linked inquiry still in progress, as the hourly poll does. null while switched off.
export async function syncReservations() {
  try { return (await reservationSend<{ data: { checked: number; changed: number } }>("/reservation/sync", "POST", {})).data; }
  catch (err) { if (err instanceof ReservationApiError && err.code === "not_enabled") return null; throw err; }
}
